import { useLocation } from "react-router-dom";
import React, { useRef } from "react";

import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import multiMonthPlugin from "@fullcalendar/multimonth";
import moment from "moment-timezone";

export default function PreferredExamCalendar(props) {
	const { dataStudentExam, setTableFilter, setExamSchedule } = props;

	const location = useLocation();

	moment.tz.setDefault("Asia/Manila");
	const calendarRef = useRef(null);
	dayjs.extend(isSameOrBefore);

	let eventCount = 0;

	const currentDate = dayjs().startOf("day");
	const threeMonthsAgo = dayjs().subtract(3, "months");

	const eventData = dataStudentExam?.dataExamSchedules?.map((item) => {
		const {
			exam_date,
			available_slots,
			slots,
			time_in,
			time_in_meridiem,
			time_out,
			time_out_meridiem,
		} = item;

		const isBeforeCurrentDate = dayjs(exam_date).isBefore(currentDate);

		return {
			title: `Exam Schedule  ${available_slots} / ${slots}`,
			time: `( ${time_in} ${time_in_meridiem} - ${time_out} ${time_out_meridiem} )`,
			start: dayjs(
				`${exam_date} ${time_in} ${time_in_meridiem}`,
				"YYYY-MM-DD hh:mm A"
			).format("YYYY-MM-DD HH:mm:ss"),
			end: dayjs(
				`${exam_date} ${time_out} ${time_out_meridiem}`,
				"YYYY-MM-DD HH:mm:ss"
			).format("YYYY-MM-DD HH:mm:ss"),
			color: isBeforeCurrentDate
				? "grey"
				: available_slots === 0
				? "#FE0000"
				: "green",
			classNames: available_slots === 0 ? "no-available" : "available",
			item: item,
		};
	});

	const currentDay = new Date().toLocaleString("en-us", {
		weekday: "short",
	});

	const isWalkInPath = location.pathname === "/applicants/walk-in";

	const onSelectDate = (arg) => {
		// Restrict to only dates with available slots
		const selectedEvent = eventData.find(
			(event) =>
				dayjs(event.start).isSame(arg.start, "day") &&
				event.item.available_slots > 0
		);

		if (selectedEvent) {
			if (
				location.pathname.includes("add") ||
				location.pathname.includes("edit")
			) {
				setExamSchedule(selectedEvent.item.id);
			} else {
				setTableFilter((ps) => ({
					...ps,
					exam_schedule_id: selectedEvent.item.id,
				}));
			}
		}
	};

	return (
		<div className="container-exam-calendar">
			<FullCalendar
				// ref to the calendar to access the calendar object
				ref={calendarRef}
				height="650px"
				dayMaxEvents={true}
				displayEventTime={false}
				events={eventData}
				eventContent={(arg) => {
					const { event } = arg;
					const { title } = event._def;
					const { color } = event._def.ui;
					const { time } = event._def.extendedProps;

					return (
						<div
							className="event-content"
							style={{
								backgroundColor: color,
								borderRadius: "5px",
								padding: "5px",
								color: "white",
							}}
						>
							{title}
							<br />
							{time}
						</div>
					);
				}}
				plugins={[
					dayGridPlugin,
					timeGridPlugin,
					interactionPlugin,
					multiMonthPlugin,
				]}
				headerToolbar={{
					left: "prev,today,next",
					center: "title",
					right: "dayGridMonth,timeGridWeek,timeGridDay",
				}}
				views={{
					dayGridMonth: {},
					timeGridWeek: {
						nowIndicator: true,
						slotMinTime: "06:00:00",
						slotMaxTime: "22:00:00",
					},
					timeGridDay: {
						nowIndicator: true,
						slotMinTime: "06:00:00",
						slotMaxTime: "22:00:00",
					},
				}}
				eventDidMount={({ event }) =>
					moment(event.start).isSame(new Date(), "day") && eventCount++
				}
				datesSet={({ startStr, endStr, args }) => {
					document
						// highlight the current day, by adding a class to the header
						.querySelectorAll(".fc-col-header-cell")
						.forEach(
							(header) =>
								header.textContent === currentDay &&
								header.classList.add("current-day-header")
						);

					const start = new Date(startStr);

					document.querySelectorAll(".fc-day").forEach((cell, index) => {
						const date = new Date(
							start.getTime() + index * 24 * 60 * 60 * 1000
						);

						if (calendarRef.current) {
							const eventsOnThisDay = calendarRef.current
								.getApi()
								.getEvents()
								.filter((event) => event.start.getDate() === date.getDate());
							if (eventsOnThisDay.length > 0) {
								const badge = Object.assign(document.createElement("badge"), {
									className: "badge",
								});

								// Reset event count
								const countTextElement = cell.querySelector(".countText");
								const badgeElement = cell.querySelector(".badge");
								if (countTextElement) countTextElement.remove();
								if (badgeElement) badgeElement.remove();

								cell.append(badge);

								if (calendarRef.current.getApi().view.type !== "dayGridMonth") {
									const countText = Object.assign(
										document.createElement("span"),
										{
											className: "countText",
											textContent: ` ${eventsOnThisDay.length} Schedule`,
										}
									);
									cell.append(countText);
								}
							}
						}
					});
				}}
				dayCellClassNames={(arg) => {
					const selectedDate = dayjs(arg.date);
					if (isWalkInPath) {
						if (selectedDate.isSameOrAfter(threeMonthsAgo)) {
							return "fc-day-selected";
						}
					} else if (location.pathname === "/payments") {
						if (selectedDate.isSameOrAfter(threeMonthsAgo)) {
							return "fc-day-selected";
						}
					} else {
						if (selectedDate.isBefore(currentDate)) {
							return "fc-day-disabled";
						}
					}
					return "";
				}}
				selectable={true}
				selectAllow={(selectInfo) => {
					if (isWalkInPath) {
						return true; // Enable all dates
					} else if (location.pathname === "/payments") {
						return true; // Enable all dates
					} else {
						return dayjs(selectInfo.start).isSameOrAfter(currentDate);
					}
				}}
				select={onSelectDate}
				eventClick={(arg) => {
					let item = arg.event.extendedProps.item;
					if (isWalkInPath) {
						if (
							!location.pathname.includes("add") ||
							!location.pathname.includes("edit")
						) {
							setTableFilter((ps) => ({
								...ps,
								exam_schedule_id: item.id,
							}));
						} else {
							setExamSchedule(item.id);
						}
					} else {
						if (dayjs(item.exam_date).isBefore(currentDate)) {
							alert("This date is locked and cannot be selected.");
							return;
						}

						if (item.available_slots > 0) {
							if (
								!location.pathname.includes("add") &&
								!location.pathname.includes("edit")
							) {
								setTableFilter((ps) => ({
									...ps,
									exam_schedule_id: item.id,
								}));
							} else {
								setExamSchedule(item.id);
							}
						}
					}
				}}
			/>
		</div>
	);
}
